import { useMemo } from "react";
import { Col, Row } from "antd";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";


const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const { legalName, websiteUrl, brokerAddress, supportEmail, brokerName } = useSelector((state: any) => state.theme) as any;
  const lastUpdated = useMemo(() => {
    return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }, []);
  const section = (index: number, title: string, content: React.ReactNode) => <Row key={index} gutter={[0, 16]}>
    <Col span={24} className="pp-subheading">{index + 1}. {title}</Col>
    <Col span={24} className="pp-text">{content}</Col>
  </Row>;

const privacy_policy_content = [
  {
    title: 'Scope of the Privacy Policy',
    content: <span>This Privacy Policy applies to all personal and non-personal information collected through our Services. It also applies to certain offline interactions, such as customer support, email, or telephonic communications, when such interactions relate to the provision of our Services.</span>
  },
  {
    title: 'Information We Collect',
    content: <Row gutter={[0, 16]}>
      <Col span={24}>
        We may collect the following types of information from you:
      </Col>
      <Col span={24}>
        <ol>
          <li>
            Personal Information<br />
            Information that identifies you directly, including:
            <ul>
              <li>Name, email address, phone number</li>
              <li>Government-issued IDs such as PAN, Aadhaar</li>
              <li>Employment and organization details (if registering via employer)</li>
              <li>Date of birth and demographic data</li>
            </ul>
          </li>
          <li>
            Transactional Information<br />
            Information related to your interactions with our Services, such as:
            <ul>
              <li>Policy and coverage details</li>
              <li>Payment and transaction data (e.g., payment method, transaction date, merchant details)</li>
              <li>Claims history and documentation</li>
            </ul>
          </li>
          <li>
            Technical and Device Information<br />
            Automatically collected information including:
            <ul>
              <li>Device type, operating system, browser, IP address</li>
              <li>Cookies, web beacons, and tracking technologies</li>
              <li>Usage patterns on the Services</li>
            </ul>
          </li>
          <li>
            User-Generated Content<br />
            Content that you voluntarily provide or upload to the platform, including:
            <ul>
              <li>Documents, forms, text, photos, or videos</li>
              <li>Comments, feedback, or surveys</li>
            </ul>
          </li>
          <li>
            Third-Party Information<br />
            Information obtained from:
            <ul>
              <li>Insurance partners, service providers, and employers</li>
              <li>Integrations with cloud storage or other tools</li>
              <li>Publicly available sources or third-party APIs</li>
            </ul>
          </li>
        </ol>
      </Col>
    </Row>
  },
  {
    title: 'How We Use Your Information',
    content: <Row gutter={[0, 16]}>
      <Col span={24}>
        We may use your information for the following purposes:
      </Col>
      <Col span={24}>
        <ul>
          <li>To provide, maintain, and improve our Services</li>
          <li>To facilitate policy issuance, claims processing, and renewals</li>
          <li>To authenticate accounts and verify identity</li>
          <li>To send updates, alerts, or promotional communications (with your consent)</li>
          <li>To comply with applicable laws and regulatory requirements</li>
          <li>For fraud detection, security, and risk management</li>
          <li>For internal analytics, business intelligence, and performance improvement</li>
        </ul>
      </Col>
    </Row>
  },
  {
    title: 'How We Share Your Information',
    content: <Row gutter={[0, 16]}>
      <Col span={24}>
        We may share your information with:
      </Col>
      <Col span={24}>
        <ul>
          <li>Insurance Partners & Service Providers: For underwriting, policy servicing, and claims processing.</li>
          <li>Bharatsure: As the technology provider, Bharatsure may process personal information on behalf of {brokerName} for platform functionality and hosting.</li>
          <li>Regulatory Authorities: When required by law or for compliance purposes.</li>
          <li>Business Transfers: If {brokerName} undergoes a merger, acquisition, or other change of control.</li>
        </ul>
      </Col>
      <Col span={24}>
        We do not sell personal information to third parties.
      </Col>
    </Row>
  },
  {
    title: 'Data Retention',
    content: <span>We retain your personal data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy or as required by law. Different categories of data may be retained for different durations based on purpose, user type, and regulatory obligations.</span>
  },
  {
    title: 'Security',
    content: <span>We implement administrative, technical, and managerial measures to protect your data. BharatSure, as the technology provider, follows industry-standard security practices, including encryption, access controls, and regular audits, to safeguard your information.</span>
  },
  {
    title: 'Your Rights',
    content: <Row gutter={[0, 16]}>
      <Col span={24}>
      Depending on your jurisdiction, you may have the following rights:
      </Col>
      <Col span={24}>
        <ul>
          <li>Right to access, correct, or delete your personal information</li>
          <li>Right to restrict or object to processing</li>
          <li>Right to data portability</li>
          <li>Right to withdraw consent for processing</li>
          <li>Right to opt-out of promotional communications</li>
        </ul>
      </Col>
      {supportEmail && <Col span={24}>
      To exercise your rights, contact {supportEmail}.
      </Col>}
    </Row>
  },
  {
    title: 'Children\'s Privacy',
    content: <span>Our Services are not directed at children under 18 years of age. We do not knowingly collect personal information from minors. If we become aware of such data, it will be deleted promptly.</span>
  },
  {
    title: 'Links to Other Websites',
    content: <span>Our Services may contain links to third-party websites. We are not responsible for the privacy practices of these third parties. You are encouraged to review their privacy policies before sharing personal information.</span>
  },
  {
    title: 'Changes to this Policy',
    content: <span>We may update this Privacy Policy periodically. Updates will be posted on this page with a “Last Updated” date. Significant changes may be communicated via email or notifications within the Services.</span>
  },
  {
    title: 'Contact Information',
    content: <Row gutter={[0, 16]}>
      <Col span={24}>Legal Department</Col>
      {legalName && <Col span={24}>{legalName}</Col>}
      {brokerAddress && <Col span={24}>{brokerAddress}</Col>}
      {supportEmail && <Col span={24}>Email: {supportEmail}</Col>}
    </Row>
  },
]

  return <div className="pp-content pp-text">
    <Row gutter={[0, 32]}>
      <Row gutter={[0, 24]}>
        <Col span={24} className="pp-title">Privacy Policy</Col>
        <Col span={24} className="pp-text" style={{ fontWeight: 500 }}>Last updated on {lastUpdated}</Col>
        <Col span={24} className="pp-text">
          This Privacy Policy governs the information collection, use, and disclosure practices of {legalName} (“{brokerName},” “we,” “us,” “our”) on the website <a href={websiteUrl} target="_blank">{websiteUrl}</a> and related applications and services (collectively, “Services”).
          The Services are powered and supported by BharatSure (Safetynet Innovations Private Limited), which provides the technology platform and infrastructure to enable the delivery of these Services.
          <br /> <br />
          For details about the service terms that govern your usage of our platform, please see our <span onClick={() => navigate('/terms-and-conditions')} style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}>Terms and Conditions</span>.
          <br /> <br />
          We are committed to protecting your privacy and handling your personal data in accordance with applicable laws, regulations, and industry best practices.
        </Col>
      </Row>
      <Row gutter={[0, 24]}>
        {privacy_policy_content.map((item, index) => section(index, item.title, item.content))}
      </Row>
    </Row>
  </div>;
};

export default PrivacyPolicy;